// The Coldroom map's band focus (F05 UF-03): tapping an OK / Warning /
// Critical / Over tile shows only the cells AT THAT BAND OR WORSE, so the old
// fruit is found from the tile instead of by eye across the grid.
//
// A cell's band is its WORST item's band (the cell colour), so "Critical or
// worse" keeps a cell holding one critical pallet among fresh ones — the same
// rule that turns that cell red. The tile counts ITEMS and includes the stock
// in the room's overflow (mig 20261021100001); that stock has no cell, so the
// focus line names it separately — as the pallets and bins the cards below
// list — rather than letting the tile and the grid disagree in silence.
import type { AgeBand } from '@/lib/aging'

export const AGE_BANDS = ['ok', 'warning', 'critical', 'over'] as const satisfies readonly AgeBand[]

export const BAND_RANK: Record<AgeBand, number> = { ok: 0, warning: 1, critical: 2, over: 3 }

/** The tiles' own words, so the focus line quotes what was tapped. */
export const BAND_TILE_LABEL: Record<AgeBand, string> = {
  ok: 'OK', warning: 'Warning', critical: 'Critical', over: 'Over',
}

/** Does a cell whose worst band is `worst` stay on the map under `focus`?
 *  An empty cell (worst null) never does. */
export function cellInBandFocus(worst: AgeBand | null, focus: AgeBand): boolean {
  return worst != null && BAND_RANK[worst] >= BAND_RANK[focus]
}

/** "Showing 4 cells at Critical or worse · 3 pallets and 1 bin off the grid,
 *  listed below". The off-grid figure is what the Overflow / no-cell cards
 *  below actually list — EVERY band, since those cards are not filtered by the
 *  lit tile — and it names pallets and bins rather than adding them to the
 *  cell count's unit (review, 7 Oct: "+N" counted at-or-worse items while the
 *  cards listed every one, and read as more cells). */
export function bandFocusText(focus: AgeBand, cells: number, offGrid: { pallets: number; bins: number }): string {
  const band = `${BAND_TILE_LABEL[focus]}${focus === 'over' ? '' : ' or worse'}`
  const head = cells === 0
    ? `No cells at ${band}`
    : `Showing ${cells} cell${cells === 1 ? '' : 's'} at ${band}`
  const parts = [
    offGrid.pallets > 0 ? `${offGrid.pallets} pallet${offGrid.pallets === 1 ? '' : 's'}` : '',
    offGrid.bins > 0 ? `${offGrid.bins} bin${offGrid.bins === 1 ? '' : 's'}` : '',
  ].filter(Boolean)
  return parts.length > 0 ? `${head} · ${parts.join(' and ')} off the grid (any age), listed below` : head
}

/** The focus line while the tiles' reads are not all in: the cell count folds
 *  bins as well as pallets, so it is withheld (as the tiles show '—') rather
 *  than stated from the pallets alone. */
export function bandFocusPendingText(focus: AgeBand, state: 'loading' | 'unread'): string {
  const band = `${BAND_TILE_LABEL[focus]}${focus === 'over' ? '' : ' or worse'}`
  return state === 'loading'
    ? `Checking which cells are at ${band}…`
    : `Couldn't count the cells at ${band}`
}

/** The focus after tapping `band`'s tile: the lit tile again clears it. */
export function nextBandFocus(current: '' | AgeBand, band: AgeBand): '' | AgeBand {
  return current === band ? '' : band
}

/** On a phone the tiles sit under the grid: bring the grid back up when it
 *  has scrolled off the top, so the focus is seen. A missing grid is a no-op. */
export function revealGridIfScrolledPast(grid: Pick<HTMLElement, 'getBoundingClientRect' | 'scrollIntoView'> | null): void {
  if (grid && grid.getBoundingClientRect().top < 0) grid.scrollIntoView({ block: 'start' })
}

/** What tapping a grid cell does. A cell hidden by a band focus shows as an
 *  empty ghost but may hold stock: it must NOT open the empty-cell "Place on"
 *  sheet (D3). Place only opens on a cell whose occupancy is READ. */
export function cellTapAction(
  hidden: boolean, total: number, gridUsable: boolean,
): 'detail' | 'place' | 'none' {
  if (hidden) return 'none'
  if (total > 0) return 'detail'
  return gridUsable ? 'place' : 'none'
}
